import type { OrderRecordList, OrderType } from '@navio/nsdk';
import { keepPreviousData } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiIcon, type NoddiIconName } from 'noddi-ui-common';
import {
  ApiErrorPageInternal,
  NoddiBadge,
  NoddiFilterChip,
  NoddiTable,
  toggleFilterValue,
  usePaginationQueryParam
} from 'noddi-ui-web';
import { useState } from 'react';

import { getOrderTableColumns } from '../../../hooks/filters/Order/getOrderTableColumns';
import type { InvoiceRoutes } from '../types';

/**
 * Only the two types with a live creation path.
 *
 * `RIM_QUOTE` and adapter ad-hoc orders have no creation code and no
 * `Order.rim_quote` FK — offering them would render two permanently empty
 * options.
 */
const ORDER_TYPES: { value: OrderType; label: string; icon: NoddiIconName }[] = [
  { value: 'booking', label: 'Booking', icon: 'CalendarDays' },
  { value: 'tire_quote', label: 'Shop purchase', icon: 'ShoppingCart' }
];

/**
 * Labels and icons live here rather than coming from the record.
 *
 * `OrderRecordList.orderType` is a bare enum value, not a `ChoiceRecord`, so it
 * carries no label — and the generated `OrderTypeLabels` map currently holds the
 * raw values, so it would print "tire_quote" to staff.
 */
const ORDER_TYPE_BY_VALUE = new Map(ORDER_TYPES.map((orderType) => [orderType.value as string, orderType]));

/**
 * A shop purchase has no booking, so every booking-shaped column on it is empty.
 *
 * Neutral badge plus the chip's own icon: the icon is what tells the two types
 * apart, in the column and in the filter above it, so neither needs a color of
 * its own to carry the same meaning twice.
 */
const typeColumn: ColumnDef<OrderRecordList> = {
  // Not `orderType`: the shared "Order reference" column already claims that id
  // through its accessorKey, and duplicate column ids break React's row keys.
  id: 'orderTypeBadge',
  header: 'Type',
  cell: ({ row }) => {
    const orderType = ORDER_TYPE_BY_VALUE.get(row.original.orderType);
    return (
      <NoddiBadge variant='outline'>
        {orderType && <NoddiIcon name={orderType.icon} size='xSmall' />}
        {orderType?.label ?? row.original.orderType}
      </NoddiBadge>
    );
  }
};

type Props = {
  invoiceId: number;
  routes: InvoiceRoutes;
};

/**
 * The orders this invoice covers.
 *
 * Orders are what an invoice is made of — they carry the money, and a shop
 * purchase has no booking at all, so a booking-shaped view structurally cannot
 * show one. Reuses the orders list columns so an order reads the same here as on
 * the orders page, with a Type column in front because telling booking work from
 * a shop purchase is the distinction that matters on an invoice.
 */
export const InvoiceOrdersTab = ({ invoiceId, routes }: Props) => {
  const { pagination, setPagination } = usePaginationQueryParam({ initialPageSize: 10 });
  const [orderTypes, setOrderTypes] = useState<OrderType[]>([]);

  // Narrowing the type shrinks the result set, so staying on page 3 can request
  // a page past the end and render an empty table while matching orders exist.
  const setOrderTypesFromPageOne = (next: OrderType[]) => {
    setOrderTypes(next);
    if (pagination.pageIndex !== 0) {
      setPagination({ ...pagination, pageIndex: 0 });
    }
  };

  const {
    data: orders,
    isPending,
    isFetching,
    error
  } = noddiAsync.useGet({
    type: URLKeys.getPaginatedOrders,
    input: {
      invoiceIds: [invoiceId],
      // Filtered server-side: the tab is paginated, so narrowing the rendered
      // page would leave the count and the pager describing different sets.
      ...(orderTypes.length > 0 && { orderTypes }),
      ...pagination
    },
    queryConfig: {
      placeholderData: keepPreviousData
    }
  });

  // Per-type totals for the chips, scoped to the invoice but NOT to the active
  // selection, so each chip always shows its own total and picking one does not
  // zero the other. Single-row pages — the chips only read `.count`.
  const { data: bookingCount, error: _bookingCountError } = noddiAsync.useGet({
    type: URLKeys.getPaginatedOrders,
    // `pageIndex`, not `page` — that is the input the paginated orders endpoint takes.
    input: { invoiceIds: [invoiceId], orderTypes: ['booking'], pageIndex: 0, pageSize: 1 }
  });
  const { data: shopCount, error: _shopCountError } = noddiAsync.useGet({
    type: URLKeys.getPaginatedOrders,
    input: { invoiceIds: [invoiceId], orderTypes: ['tire_quote'], pageIndex: 0, pageSize: 1 }
  });
  const countByType = new Map<OrderType, number | undefined>([
    ['booking', bookingCount?.count],
    ['tire_quote', shopCount?.count]
  ]);

  if (error) {
    return <ApiErrorPageInternal error={error} />;
  }

  return (
    <div className='flex flex-col gap-4'>
      {/* Chips rather than a filter dropdown: there are exactly two types, and
          their counts are the fastest way to see what an invoice is made of. */}
      <div className='flex flex-wrap gap-2'>
        {ORDER_TYPES.map(({ value, label, icon }) => (
          <NoddiFilterChip
            key={value}
            icon={icon}
            label={label}
            isActive={orderTypes.includes(value)}
            allActive={orderTypes.length === 0}
            onClick={() => setOrderTypesFromPageOne(toggleFilterValue(orderTypes, value))}
            count={countByType.get(value) ?? '-'}
          />
        ))}
      </div>

      <NoddiTable
        pagination={pagination}
        setPagination={setPagination}
        totalCount={orders?.count}
        isPending={isPending}
        isFetching={isFetching}
        data={orders?.results}
        searchPlaceholder='Search by order reference or customer...'
        getRowId={(row) => row.id}
        getUrlPath={(row) => routes.orderUrl(row.id)}
        columns={[typeColumn, ...getOrderTableColumns()]}
      />
    </div>
  );
};
